// src/services/reviewModerationService.js - COMPLETE AND FIXED VERSION
import { db, auth } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  getDoc,
  setDoc,
  addDoc
} from 'firebase/firestore';

class ReviewModerationService {
  constructor() {
    this.reviewsCollection = 'reviews';
  }

  /**
   * Check if current user is an admin - integrated with existing admin system
   */
  async isCurrentUserAdmin() {
    const user = auth.currentUser;
    if (!user) return false;

    // Use the same logic as your existing AdminDashboard
    return user.email === 'amirsafary9@gmail.com';
  }

  /**
   * Get all reviews for moderation (admin only)
   */
  async getAllReviewsForModeration(options = {}) {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      const {
        status = 'all', // all, published, pending, hidden, reported
        limitCount = 50,
        sortBy = 'newest'
      } = options;

      let reviewQuery = collection(db, this.reviewsCollection);

      // Apply status filter
      if (status !== 'all') {
        if (status === 'reported') {
          reviewQuery = query(reviewQuery, where('reportCount', '>', 0));
        } else {
          reviewQuery = query(reviewQuery, where('status', '==', status));
        }
      }

      // Apply sorting
      switch (sortBy) {
        case 'newest':
          reviewQuery = query(reviewQuery, orderBy('createdAt', 'desc'));
          break;
        case 'oldest':
          reviewQuery = query(reviewQuery, orderBy('createdAt', 'asc'));
          break;
        case 'most_reported':
          reviewQuery = query(reviewQuery, orderBy('reportCount', 'desc'), orderBy('createdAt', 'desc'));
          break;
        case 'rating_high':
          reviewQuery = query(reviewQuery, orderBy('rating', 'desc'), orderBy('createdAt', 'desc'));
          break;
        case 'rating_low':
          reviewQuery = query(reviewQuery, orderBy('rating', 'asc'), orderBy('createdAt', 'desc'));
          break;
      }

      reviewQuery = query(reviewQuery, limit(limitCount));

      const querySnapshot = await getDocs(reviewQuery);
      const reviews = [];

      querySnapshot.forEach((doc) => {
        reviews.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        });
      });

      return reviews;
    } catch (error) {
      console.error('Error fetching reviews for moderation:', error);
      throw error;
    }
  }

  /**
   * Update review status (admin only)
   */
  async updateReviewStatus(reviewId, newStatus, moderationNote = '') {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      const reviewRef = doc(db, this.reviewsCollection, reviewId);
      const user = auth.currentUser;

      const updateData = {
        status: newStatus,
        moderatedAt: serverTimestamp(),
        moderatedBy: user.uid,
        moderatorEmail: user.email,
        moderationNote: moderationNote,
        updatedAt: serverTimestamp()
      };

      // If hiding a review, add hidden reason
      if (newStatus === 'hidden') {
        updateData.hiddenReason = moderationNote || 'Content moderated by admin';
      }

      await updateDoc(reviewRef, updateData);

      // Log admin action
      await this.logAdminAction('update_review_status', {
        reviewId,
        newStatus,
        moderationNote
      });

      return { success: true, message: 'Review status updated successfully' };
    } catch (error) {
      console.error('Error updating review status:', error);
      throw error;
    }
  }

  /**
   * Delete review permanently (admin only)
   */
  async deleteReview(reviewId, reason = '') {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      const reviewRef = doc(db, this.reviewsCollection, reviewId);
      const user = auth.currentUser;

      // Get review data before deletion for logging
      const reviewDoc = await getDoc(reviewRef);
      const reviewData = reviewDoc.exists() ? reviewDoc.data() : {};

      // Log deletion for audit trail
      console.log(`Review ${reviewId} deleted by admin ${user.email}. Reason: ${reason}`);

      await deleteDoc(reviewRef);

      // Log admin action
      await this.logAdminAction('delete_review', {
        reviewId,
        reason,
        deletedReview: {
          userId: reviewData.userId,
          userEmail: reviewData.userEmail,
          rating: reviewData.rating,
          comment: reviewData.comment?.substring(0, 100) + '...' // First 100 chars for logging
        }
      });

      return { success: true, message: 'Review deleted successfully' };
    } catch (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  }

  /**
   * Clear reports for a review (admin only)
   */
  async clearReports(reviewId) {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      const reviewRef = doc(db, this.reviewsCollection, reviewId);

      await updateDoc(reviewRef, {
        reportCount: 0,
        lastReportReason: null,
        lastReportedAt: null,
        reportsCleared: true,
        reportsClearedAt: serverTimestamp(),
        reportsClearedBy: auth.currentUser.uid,
        updatedAt: serverTimestamp()
      });

      // Log admin action
      await this.logAdminAction('clear_review_reports', {
        reviewId
      });

      return { success: true, message: 'Reports cleared successfully' };
    } catch (error) {
      console.error('Error clearing reports:', error);
      throw error;
    }
  }

  /**
   * Get moderation statistics (admin only)
   */
  async getModerationStats() {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      const reviewsRef = collection(db, this.reviewsCollection);

      // Get all reviews
      const allReviewsQuery = query(reviewsRef);
      const allReviewsSnapshot = await getDocs(allReviewsQuery);

      // Get reported reviews
      const reportedQuery = query(reviewsRef, where('reportCount', '>', 0));
      const reportedSnapshot = await getDocs(reportedQuery);

      // Get pending reviews
      const pendingQuery = query(reviewsRef, where('status', '==', 'pending'));
      const pendingSnapshot = await getDocs(pendingQuery);

      // Get hidden reviews
      const hiddenQuery = query(reviewsRef, where('status', '==', 'hidden'));
      const hiddenSnapshot = await getDocs(hiddenQuery);

      // Calculate stats
      const stats = {
        totalReviews: allReviewsSnapshot.size,
        publishedReviews: 0,
        pendingReviews: pendingSnapshot.size,
        hiddenReviews: hiddenSnapshot.size,
        reportedReviews: reportedSnapshot.size,
        averageRating: 0,
        reviewsThisWeek: 0,
        reviewsThisMonth: 0
      };

      let totalRating = 0;
      const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

      allReviewsSnapshot.forEach((doc) => {
        const review = doc.data();
        const createdAt = review.createdAt?.toDate();

        if (review.status === 'published') {
          stats.publishedReviews++;
          totalRating += review.rating;
        }

        if (createdAt && createdAt > oneWeekAgo) {
          stats.reviewsThisWeek++;
        }

        if (createdAt && createdAt > oneMonthAgo) {
          stats.reviewsThisMonth++;
        }
      });

      if (stats.publishedReviews > 0) {
        stats.averageRating = (totalRating / stats.publishedReviews).toFixed(1);
      }

      return stats;
    } catch (error) {
      console.error('Error fetching moderation stats:', error);
      throw error;
    }
  }

  /**
   * Bulk action on reviews (admin only)
   */
  async bulkAction(reviewIds, action, note = '') {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      const results = [];

      for (const reviewId of reviewIds) {
        try {
          switch (action) {
            case 'publish':
              await this.updateReviewStatus(reviewId, 'published', note);
              break;
            case 'hide':
              await this.updateReviewStatus(reviewId, 'hidden', note);
              break;
            case 'delete':
              await this.deleteReview(reviewId, note);
              break;
            case 'clear_reports':
              await this.clearReports(reviewId);
              break;
            default:
              throw new Error(`Unknown action: ${action}`);
          }
          results.push({ reviewId, success: true });
        } catch (error) {
          results.push({ reviewId, success: false, error: error.message });
        }
      }

      // Log bulk action
      await this.logAdminAction('bulk_review_action', {
        action,
        reviewIds,
        note,
        results
      });

      return results;
    } catch (error) {
      console.error('Error performing bulk action:', error);
      throw error;
    }
  }

  /**
   * Auto-moderate based on keywords (admin setup)
   */
  async setupAutoModeration(rules) {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      const rulesRef = doc(db, 'moderationRules', 'autoModerationRules');

      await setDoc(rulesRef, {
        rules: rules,
        createdBy: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { success: true, message: 'Auto-moderation rules updated' };
    } catch (error) {
      console.error('Error setting up auto-moderation:', error);
      throw error;
    }
  }

  /**
   * Log admin actions for audit trail
   */
  async logAdminAction(action, details = {}) {
    if (!(await this.isCurrentUserAdmin())) return;

    try {
      const adminLogRef = collection(db, 'adminLogs');
      await addDoc(adminLogRef, {
        action,
        details,
        adminEmail: auth.currentUser.email,
        adminUid: auth.currentUser.uid,
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent,
        category: 'review_moderation'
      });
    } catch (error) {
      console.error('Error logging admin action:', error);
    }
  }

  /**
   * Format relative time for admin interface
   */
  formatRelativeTime(date) {
    if (!date) return 'Unknown';

    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Gerade eben';
    if (diffInSeconds < 3600) return `vor ${Math.floor(diffInSeconds / 60)} Min.`;
    if (diffInSeconds < 86400) return `vor ${Math.floor(diffInSeconds / 3600)} Std.`;
    if (diffInSeconds < 2592000) return `vor ${Math.floor(diffInSeconds / 86400)} Tagen`;
    if (diffInSeconds < 31536000) return `vor ${Math.floor(diffInSeconds / 2592000)} Monaten`;
    return `vor ${Math.floor(diffInSeconds / 31536000)} Jahren`;
  }

  /**
   * Get review details for moderation
   */
  async getReviewDetails(reviewId) {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      const reviewRef = doc(db, this.reviewsCollection, reviewId);
      const reviewDoc = await getDoc(reviewRef);

      if (!reviewDoc.exists()) {
        throw new Error('Review not found');
      }

      const reviewData = reviewDoc.data();
      return {
        id: reviewDoc.id,
        ...reviewData,
        createdAt: reviewData.createdAt?.toDate(),
        updatedAt: reviewData.updatedAt?.toDate(),
        moderatedAt: reviewData.moderatedAt?.toDate(),
        lastReportedAt: reviewData.lastReportedAt?.toDate()
      };
    } catch (error) {
      console.error('Error fetching review details:', error);
      throw error;
    }
  }

  /**
   * Update review statistics (internal method)
   */
  async updateReviewStats() {
    try {
      const stats = await this.getModerationStats();
      const statsRef = doc(db, 'siteStats', 'reviews');

      await setDoc(statsRef, {
        ...stats,
        lastUpdated: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating review stats:', error);
    }
  }

  /**
   * Generate user initials for display
   */
  generateInitials(firstName, lastName, displayName) {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (displayName) {
      const names = displayName.split(' ');
      if (names.length >= 2) {
        return `${names[0].charAt(0)}${names[1].charAt(0)}`.toUpperCase();
      }
      return names[0].charAt(0).toUpperCase();
    }
    return 'NU'; // Anonymous User in German
  }
}

export default new ReviewModerationService();